import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import {
  TRAY_LAZY_CHUNK_SIZE,
  nextTrayVisibleCount,
  sliceTrayWindow,
  trayColumnCount,
} from '@/lib/tray/tray-lazy-window'

export type UseTrayLazyWindowOptions<T> = {
  /** The full, already filtered + sorted result list. */
  items: T[]
  /** Scroll container the sentinel is observed against. */
  scrollRootRef: RefObject<HTMLElement | null>
  /** Any change to this value collapses the window back to the first chunk and scrolls the root
   *  back to the top (e.g. a filter, sort, category or source change). Callers typically pass a serialized snapshot of their controls. */
  resetKey: string
  /** One column up to this many results, two beyond. Defaults to the tray default
   *  (`TRAY_SINGLE_COLUMN_MAX`); a tray with its own threshold (Templates) passes it here so
   *  `columns` is the one answer and the panel needn't compute it again. */
  singleColumnMax?: number
}

export type TrayLazyWindow<T> = {
  visibleItems: T[]
  hasMore: boolean
  columns: 1 | 2
  /** Attach to a zero-height element after the grid; rendered only while `hasMore`. */
  sentinelRef: RefObject<HTMLDivElement | null>
}

/**
 * Client-side windowing for a right-panel tray grid: shows `TRAY_LAZY_CHUNK_SIZE` items, and
 * grows by one chunk each time the sentinel scrolls within 120px of the scroll root.
 */
export function useTrayLazyWindow<T>({
  items,
  scrollRootRef,
  resetKey,
  singleColumnMax,
}: UseTrayLazyWindowOptions<T>): TrayLazyWindow<T> {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(TRAY_LAZY_CHUNK_SIZE)
  const total = items.length

  useEffect(() => {
    setVisibleCount(TRAY_LAZY_CHUNK_SIZE)
    // The window collapsed to a new result set; keep the old scroll offset and the user lands
    // mid-list (or past the end) of the new one.
    if (scrollRootRef.current) {
      scrollRootRef.current.scrollTop = 0
    }
  }, [resetKey, scrollRootRef])

  const visibleItems = useMemo(() => sliceTrayWindow(items, visibleCount), [items, visibleCount])
  const hasMore = visibleCount < total

  useEffect(() => {
    const root = scrollRootRef.current
    const sentinel = sentinelRef.current

    if (!root || !sentinel || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return

        setVisibleCount((current) => nextTrayVisibleCount(current, total))
      },
      { root, rootMargin: '120px 0px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, scrollRootRef, total, visibleItems.length])

  return { visibleItems, hasMore, columns: trayColumnCount(total, singleColumnMax), sentinelRef }
}
